import { Navigate } from 'react-router-dom';
import { COMPANY_SCOPE, useSelection } from '@/app/SelectionContext';
import { useAlerts, useKpis } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { KPICard } from '@/components/KPICard';
import { KpiGridSkeleton } from '@/components/LoadingScreen';
import { ErrorState } from '@/components/ErrorState';
import { NoFinancialData } from '@/components/NoFinancialData';
import { Card, SectionTitle } from '@/components/Card';
import { Badge, type BadgeTone } from '@/components/Badge';
import {
  ChartCard,
  ExpenseStackChart,
  MarginTrendChart,
  ProfitBridgeChart,
  ProfitTrendChart,
  RevenueTrendChart,
  SingleSeriesChart,
  type BridgeStep,
} from '@/components/charts';
import { buildPoints, monthlySeriesLabels, monthlySeriesValues, movingAverage3 } from '@/utils/series';
import {
  formatAmd,
  formatChange,
  formatPercent,
  formatPoints,
  periodLabel,
  toNumber,
} from '@/utils/format';
import { v, font } from '@/theme/tokens';
import type { HealthSummary } from '@/types/schemas';

/** `positiveIsGood` decides whether a positive figure is coloured green:
 * profit is a good result, an expense amount simply is not. A negative value
 * is always red regardless — see `utils/format.ts#financialTone`. */
const MONEY_KPIS = [
  ['revenue', 'Выручка', true],
  ['gross_profit', 'Валовая прибыль', true],
  ['operating_profit', 'Операционная прибыль', true],
  ['net_profit', 'Чистая прибыль', true],
  ['total_expenses', 'Все расходы', false],
  ['payroll', 'ФОТ', false],
  ['remaining', 'Остаток', true],
  ['cogs', 'Себестоимость', false],
] as const;

const RATIO_KPIS = [
  ['gross_margin', 'Валовая маржа', true],
  ['operating_margin', 'Операционная маржа', true],
  ['net_margin', 'Чистая маржа', true],
  ['payroll_ratio', 'ФОТ / Выручка', false],
] as const;

const INVERTED = new Set(['total_expenses', 'payroll', 'cogs', 'payroll_ratio']);

const EXPENSE_STRUCTURE_SERIES = [
  { key: 'production_expenses', label: 'Производство', color: 'var(--chart-amber)' },
  { key: 'fixed_expenses', label: 'Постоянные', color: 'var(--chart-blue)' },
  { key: 'operational_expenses', label: 'Эксплуатационные', color: 'var(--chart-violet)' },
] as const;

const HEALTH_TONE: Record<HealthSummary['status'], BadgeTone> = {
  ok: 'positive',
  warning: 'warning',
  critical: 'negative',
};

const HEALTH_LABEL: Record<HealthSummary['status'], string> = {
  ok: 'Без замечаний',
  warning: 'Есть предупреждения',
  critical: 'Требует внимания',
};

export function OverviewPage() {
  const { selectedUnit, selectedPeriod, scope, loading } = useSelection();
  const query = useKpis(selectedUnit?.id, selectedPeriod?.id);
  const alerts = useAlerts(selectedPeriod?.id);

  if (scope === COMPANY_SCOPE) return <Navigate to="/company" replace />;
  if (selectedUnit?.type === 'PRODUCTION_KITCHEN') return <Navigate to="/production" replace />;

  const title = selectedUnit ? `Ресторан «${selectedUnit.name}»` : 'Обзор';
  const subtitle = selectedPeriod
    ? `Обзор финансов за ${periodLabel(selectedPeriod.year, selectedPeriod.month)}`
    : 'Период не выбран';

  const data = query.data;
  const kpis = data?.kpis;
  const current = data?.current;
  const deltas = data?.previous_period_deltas;
  const series = data?.monthly_series ?? [];
  const drivers = data?.expense_drivers ?? [];
  const health = data?.health;

  const revenueDelta = toNumber(deltas?.revenue);
  const marginNow = toNumber(current?.gross_margin);
  const marginDelta = toNumber(deltas?.gross_margin);

  let charts = null;
  if (series.length > 0) {
    const labels = monthlySeriesLabels(series);
    const revenue = monthlySeriesValues(series, 'revenue');
    const gross = monthlySeriesValues(series, 'gross_profit');
    const operating = monthlySeriesValues(series, 'operating_profit');
    const net = monthlySeriesValues(series, 'net_profit');
    const grossMargin = monthlySeriesValues(series, 'gross_margin');
    const operatingMargin = monthlySeriesValues(series, 'operating_margin');
    const netMargin = monthlySeriesValues(series, 'net_margin');
    const totalExpenses = monthlySeriesValues(series, 'total_expenses');

    charts = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 16, marginTop: 16 }}>
        <ChartCard title="Выручка по месяцам">
          <RevenueTrendChart data={buildPoints(labels, { 'Выручка': revenue, 'Среднее 3 мес.': movingAverage3(revenue) })} />
        </ChartCard>
        <ChartCard title="Прибыль по месяцам">
          <ProfitTrendChart data={buildPoints(labels, { 'Валовая': gross, 'Операционная': operating, 'Чистая': net })} />
        </ChartCard>
        <ChartCard title="Маржинальность">
          <MarginTrendChart
            data={buildPoints(labels, {
              'Валовая маржа': grossMargin,
              'Операционная маржа': operatingMargin,
              'Чистая маржа': netMargin,
            })}
          />
        </ChartCard>
        <ChartCard title="Динамика расходов">
          <SingleSeriesChart
            data={buildPoints(labels, { 'Все расходы': totalExpenses })}
            name="Все расходы"
            color="var(--chart-red)"
          />
        </ChartCard>
        <ChartCard title="Структура расходов">
          <ExpenseStackChart
            data={buildPoints(
              labels,
              Object.fromEntries(
                EXPENSE_STRUCTURE_SERIES.map((s) => [s.label, monthlySeriesValues(series, s.key)]),
              ),
            )}
            series={EXPENSE_STRUCTURE_SERIES.map((s) => ({ key: s.label, color: s.color }))}
          />
        </ChartCard>
        {current && (
          <ChartCard title="Мост прибыли — текущий месяц">
            <ProfitBridgeChart steps={buildBridgeSteps(current)} />
          </ChartCard>
        )}
      </div>
    );
  }

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          selectedUnit && (
            <ExportMenu
              report="overview"
              params={{ businessUnitId: selectedUnit.id, periodId: selectedPeriod?.id }}
            />
          )
        }
      />

      {(loading || query.isPending) && <KpiGridSkeleton count={9} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {data && !data.has_data && <NoFinancialData unitName={selectedUnit?.name} />}

      {data?.has_data && kpis && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle
              right={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {health && <Badge tone={HEALTH_TONE[health.status]}>{HEALTH_LABEL[health.status]}</Badge>}
                  {alerts.data && alerts.data.alerts.length > 0 && (
                    <Badge tone="warning">{alerts.data.alerts.length} требуют внимания</Badge>
                  )}
                </div>
              }
            >
              Итоги месяца
            </SectionTitle>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: v('ink-sub') }}>
              {revenueDelta != null && (
                <div>
                  Выручка {formatAmd(toNumber(current?.revenue))} —{' '}
                  <b style={{ color: revenueDelta >= 0 ? v('positive') : v('negative') }}>
                    {formatChange(revenueDelta)}
                  </b>{' '}
                  к прошлому месяцу.
                </div>
              )}
              {marginNow != null && (
                <div>
                  Валовая маржа {formatPercent(marginNow)}
                  {marginDelta != null && <> ({formatPoints(marginDelta)} к прошлому месяцу)</>}.
                </div>
              )}
              <div>
                Чистая прибыль <b style={{ color: v('ink') }}>{formatAmd(toNumber(current?.net_profit))}</b>, чистая маржа{' '}
                <b style={{ color: v('ink') }}>{formatPercent(toNumber(current?.net_margin))}</b>.
              </div>
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
            {MONEY_KPIS.map(([key, label, positiveIsGood]) => {
              const raw = toNumber(current?.[key] ?? kpis[key]?.value);
              if (!kpis[key] && raw === null) return null;
              return (
                <KPICard
                  key={key}
                  label={label}
                  value={formatAmd(raw)}
                  rawValue={raw}
                  positiveIsGood={positiveIsGood}
                  emphasize={key === 'remaining'}
                  change={toNumber(deltas?.[key] ?? kpis[key]?.mom)}
                  invert={INVERTED.has(key)}
                  series={monthlySeriesValues(series, key)}
                />
              );
            })}
            {RATIO_KPIS.map(([key, label, positiveIsGood]) => {
              const raw = toNumber(current?.[key] ?? kpis[key]?.value);
              if (!kpis[key] && raw === null) return null;
              return (
                <KPICard
                  key={key}
                  label={label}
                  value={formatPercent(raw)}
                  rawValue={raw}
                  positiveIsGood={positiveIsGood}
                  change={toNumber(deltas?.[key] ?? kpis[key]?.mom)}
                  changeFormatter={formatPoints}
                  invert={INVERTED.has(key)}
                  series={monthlySeriesValues(series, key)}
                />
              );
            })}
          </div>

          {drivers.length > 0 && <ExpenseDrivers drivers={drivers} />}

          {charts}
        </>
      )}
    </div>
  );
}

function buildBridgeSteps(current: Record<string, string | null>): BridgeStep[] {
  const revenue = toNumber(current.revenue);
  const cogs = toNumber(current.cogs);
  const grossProfit = toNumber(current.gross_profit);
  const totalExpenses = toNumber(current.total_expenses);
  const operatingProfit = toNumber(current.operating_profit);
  const turnoverTax = toNumber(current.turnover_tax);
  const netProfit = toNumber(current.net_profit);

  return [
    { label: 'Выручка', value: revenue, isTotal: true },
    { label: 'Себестоимость', value: cogs != null ? -cogs : null },
    { label: 'Валовая прибыль', value: grossProfit, isTotal: true },
    {
      label: 'Расходы',
      value: totalExpenses != null && cogs != null ? -(totalExpenses - cogs) : null,
    },
    { label: 'Операционная прибыль', value: operatingProfit, isTotal: true },
    { label: 'Налог с оборота', value: turnoverTax != null ? -turnoverTax : null },
    { label: 'Чистая прибыль', value: netProfit, isTotal: true },
  ];
}

function ExpenseDrivers({ drivers }: {
  drivers: { code: string; name: string; current: string | null; previous: string | null; delta: string | null }[];
}) {
  return (
    <Card style={{ marginTop: 16 }}>
      <SectionTitle>Главные драйверы роста расходов</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {drivers.map((driver) => {
          const delta = toNumber(driver.delta);
          return (
            <div
              key={driver.code}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13.5 }}
            >
              <span style={{ color: v('ink-sub') }}>{driver.name}</span>
              <span style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span style={{ fontFamily: font.mono }}>{formatAmd(toNumber(driver.current))}</span>
                <b
                  style={{
                    fontFamily: font.mono,
                    color: delta != null && delta > 0 ? v('negative') : v('positive'),
                    minWidth: 90,
                    textAlign: 'right',
                  }}
                >
                  {delta != null ? `${delta >= 0 ? '+' : ''}${formatAmd(delta)}` : '—'}
                </b>
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
